import React from "react";
import { Tooltip, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";

interface HealthSegment {
  status: string;
  count: number;
  color: string;
  assets?: Array<{ id: string; name?: string }>;
}

interface SegmentedHealthBarProps {
  stats: any;
  namespace: string;
  height?: number;
}

const SegmentedHealthBar: React.FC<SegmentedHealthBarProps> = ({
  stats,
  namespace,
  height = 6,
}) => {
  const navigate = useNavigate();

  if (!stats || stats.total_assets === 0) {
    return (
      <Box
        sx={{
          height: `${height}px`,
          backgroundColor: "#e5e7eb",
          borderRadius: "4px",
          width: "100%",
        }}
      />
    );
  }

  // Define status colors and order (most severe first)
  const statusConfig = [
    { key: "crashed_count", label: "Crashed", color: "#dc2626" },
    { key: "missing_count", label: "Missing", color: "#ef4444" },
    { key: "error_count", label: "Error", color: "#f87171" },
    { key: "schema_outdated_count", label: "Schema Outdated", color: "#fb923c" },
    { key: "needs_rebuild_count", label: "Needs Rebuild", color: "#fbbf24" },
    { key: "version_mismatch_count", label: "Version Mismatch", color: "#facc15" },
    { key: "processing_count", label: "Processing", color: "#60a5fa" },
    { key: "not_published_count", label: "Not Published", color: "#9ca3af" },
    { key: "deleted_count", label: "Deleted", color: "#6b7280" },
    { key: "healthy_count", label: "Healthy", color: "#22c55e" },
  ];

  // Build segments array with percentages
  const segments: HealthSegment[] = [];

  statusConfig.forEach((config) => {
    const count = stats[config.key] || 0;
    if (count > 0) {
      segments.push({
        status: config.label,
        count: count,
        color: config.color,
        assets: stats.assets?.filter((a: any) => 
          a.status?.toLowerCase().replace(/_/g, " ") === config.label.toLowerCase()
        ) || [],
      });
    }
  });

  const handleSegmentClick = (segment: HealthSegment, event: React.MouseEvent) => {
    event.stopPropagation();
    
    // Navigate to the memory health view for this namespace
    // If we have specific asset IDs, we could pass them as query params
    navigate(`/dashboard/memory-health/${namespace}`, {
      state: { 
        filterStatus: segment.status.toLowerCase().replace(/ /g, "_"),
        assets: segment.assets 
      }
    });
  };

  return (
    <Box
      sx={{
        height: `${height}px`,
        backgroundColor: "#e5e7eb",
        borderRadius: "4px",
        overflow: "hidden",
        display: "flex",
        width: "100%",
        position: "relative",
      }}
    >
      {segments.map((segment, index) => {
        const percentage = (segment.count / stats.total_assets) * 100;
        
        return (
          <Tooltip
            key={`${segment.status}-${index}`}
            title={
              <Box sx={{ p: 0.5 }}>
                <Box sx={{ fontWeight: 600, fontSize: "0.75rem", mb: 0.5 }}>
                  {segment.status}
                </Box>
                <Box sx={{ fontSize: "0.7rem" }}>
                  {segment.count} asset{segment.count !== 1 ? "s" : ""}
                  {" "}({percentage.toFixed(1)}%)
                </Box>
                {segment.assets && segment.assets.length > 0 && (
                  <Box sx={{ mt: 0.5, fontSize: "0.65rem", opacity: 0.9 }}>
                    {segment.assets.slice(0, 3).map((asset) => (
                      <Box key={asset.id} sx={{ 
                        overflow: "hidden", 
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "200px"
                      }}>
                        • {asset.name || asset.id}
                      </Box>
                    ))}
                    {segment.assets.length > 3 && (
                      <Box sx={{ fontStyle: "italic", mt: 0.25 }}>
                        ...and {segment.assets.length - 3} more
                      </Box>
                    )}
                  </Box>
                )}
                <Box sx={{ mt: 0.5, fontSize: "0.65rem", fontStyle: "italic" }}>
                  Click to view details
                </Box>
              </Box>
            }
            arrow
            placement="top"
          >
            <Box
              onClick={(e) => handleSegmentClick(segment, e)}
              sx={{
                width: `${percentage}%`,
                backgroundColor: segment.color,
                height: "100%",
                cursor: "pointer",
                transition: "opacity 0.2s ease, transform 0.2s ease",
                transformOrigin: "center",
                "&:hover": {
                  opacity: 0.85,
                  transform: "scaleY(1.3)",
                  zIndex: 10,
                  position: "relative",
                },
                // Add a subtle separator between segments
                borderRight: index < segments.length - 1 ? "1px solid rgba(255,255,255,0.2)" : "none",
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
};

export default SegmentedHealthBar;
